import type { DefensePosition } from "../../../shared/domain/defense";
import { HARD_ONLY, POSITIONS } from "../../../shared/domain/defenseGenerator";
import { isOutOfPosition } from "../../domain/defensePresentation";
import type {
  DefenseInningDocument,
  PlayerListItem,
} from "../../shared/types/contracts";
import { Badge } from "../../shared/ui/badge";
import { Button } from "../../shared/ui/button";
import { actionKeys } from "../../hooks/actionKeys";
import type { IntentStatusView } from "../../hooks/useIntentStatus";

interface DefenseAssignmentsEditorProps {
  players: PlayerListItem[];
  activePlayers: PlayerListItem[];
  preferences: Record<string, string[]>;
  defense: DefenseInningDocument[];
  selectedInning: number;
  onSelectInning: (inning: number) => void;
  onAssign: (
    inning: number,
    position: DefensePosition,
    playerId: string | null,
  ) => void;
  actionStatus: IntentStatusView;
}

export function DefenseAssignmentsEditor({
  players,
  activePlayers,
  preferences,
  defense,
  selectedInning,
  onSelectInning,
  onAssign,
  actionStatus,
}: DefenseAssignmentsEditorProps) {
  const inning = defense[selectedInning];
  if (!inning) return null;

  return (
    <>
      <section aria-labelledby="inning-assignments-heading">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2
            id="inning-assignments-heading"
            className="m-0 font-mono text-sm font-bold tracking-[0.1em] text-warning uppercase"
          >
            INNING {selectedInning + 1}
          </h2>
          <Badge
            variant={inning.oop?.length ? "danger" : "success"}
            className="hidden sm:inline-flex"
          >
            {inning.oop?.length
              ? `${inning.oop.length} OOP`
              : "Assignments clear"}
          </Badge>
        </div>
        <div className="mb-2 flex gap-0.5 overflow-x-auto pb-1">
          {defense.map((defenseInning, index) => (
            <Button
              key={index}
              type="button"
              size="sm"
              variant={
                index === selectedInning
                  ? "selected"
                  : defenseInning.oop?.length
                    ? "scoringOut"
                    : "outline"
              }
              onClick={() => onSelectInning(index)}
              className="min-w-7 px-0"
            >
              {index + 1}
            </Button>
          ))}
        </div>

        <div className="grid overflow-hidden rounded-[var(--radius-lg)] border border-border bg-surface/60 md:grid-cols-2">
          {POSITIONS.map((position) => {
            const playerId = inning.assignment[position];
            const isOop = isOutOfPosition(position, playerId, preferences);
            const pending = actionStatus.isPending(
              actionKeys.defenseAssignment(selectedInning, position),
            );
            const unknown =
              actionStatus.phaseFor(
                actionKeys.defenseAssignment(selectedInning, position),
              ) === "outcome_unknown";
            return (
              <label
                key={position}
                className="grid min-w-0 grid-cols-[2.5rem_minmax(0,1fr)_auto] items-center gap-2 border-b border-border/70 px-2 py-1.5 md:odd:border-r"
              >
                <span className="font-mono text-base font-bold text-warning">
                  {position}
                </span>
                <select
                  value={playerId || ""}
                  disabled={pending}
                  aria-busy={pending || undefined}
                  onChange={(event) =>
                    onAssign(
                      selectedInning,
                      position,
                      event.target.value || null,
                    )
                  }
                  className={
                    isOop
                      ? "h-10 min-w-0 rounded-[var(--radius-sm)] border border-danger bg-background px-2 text-base text-danger outline-none focus:border-ring focus:ring-1 focus:ring-ring md:h-9 md:text-sm"
                      : "h-10 min-w-0 rounded-[var(--radius-sm)] border border-border bg-background px-2 text-base text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring md:h-9 md:text-sm"
                  }
                >
                  <option value="">— open —</option>
                  {activePlayers.map((player) => {
                    const rank = (preferences[player.id] || []).indexOf(
                      position,
                    );
                    if (HARD_ONLY.has(player.name) && rank < 0) return null;
                    const tag =
                      rank === 0 ? " ★" : rank > 0 ? ` (${rank + 1})` : " ⚠";
                    return (
                      <option key={player.id} value={player.id}>
                        {player.name}
                        {tag}
                      </option>
                    );
                  })}
                </select>
                <span className="min-w-10 text-right">
                  {pending ? (
                    <span className="font-mono text-[0.6rem] font-bold text-foreground-muted">
                      {unknown ? "CHECKING…" : "SAVING…"}
                    </span>
                  ) : isOop ? (
                    <Badge variant="danger">OOP</Badge>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      </section>

      <div className="rounded-[var(--radius-sm)] border border-border bg-surface/65 px-3 py-2 text-sm">
        <span className="font-mono font-bold text-danger">SITTING: </span>
        {inning.sitters.length
          ? inning.sitters
              .map((id) => players.find((player) => player.id === id)?.name)
              .filter(Boolean)
              .join(", ")
          : "nobody — full field"}
      </div>
    </>
  );
}
